import React, { useState, useMemo } from 'react';

/**
 * Sección "Hecho a mano" reutilizable.
 *
 * Props:
 *   images?: Array<{ src, alt, position? }>          — array fijo (modo simple)
 *   pool?: Array<{ src, alt, position? }>            — pool grande; el componente
 *                                                     elige `displayCount` al azar
 *                                                     y el lightbox recorre el pool completo
 *   displayCount?: number                            — fotos a mostrar en mosaico (default 7)
 *   eyebrow?: string
 *   title?: ReactNode
 *   description?: string
 */
export default function HechoAMano({
  images,
  pool,
  displayCount = 7,
  eyebrow = 'hecho a mano',
  title,
  description,
}) {
  const [lightbox, setLightbox] = useState(null);

  // En modo `pool` se mezcla una vez por montaje y se toma un subset para el mosaico.
  // El orden completo del lightbox = subset elegido + resto del pool (sin duplicar).
  const { displayed, fullSequence } = useMemo(() => {
    if (pool && pool.length > 0) {
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      const subset = shuffled.slice(0, Math.min(displayCount, shuffled.length));
      const rest = shuffled.slice(subset.length);
      return { displayed: subset, fullSequence: [...subset, ...rest] };
    }
    const arr = images || [];
    return { displayed: arr, fullSequence: arr };
  }, [images, pool, displayCount]);

  const displayCountActual = displayed.length;
  const totalInLightbox = fullSequence.length;
  const gridVariantClass = displayCountActual >= 7 ? ' sunset-grid-7' : '';

  return (
    <section id="hecho-a-mano" className="craft-section sunset-section" data-testid="craft-section">
      <div className="page-padding">
        <div className="container-large">
          <div className="craft-head sunset-head">
            <div className="text-label" style={{ color: 'var(--brand-accent)' }}>
              {eyebrow}
            </div>
            <h2 className="sm-h2 font-color-1">
              {title || (
                <>
                  Detalles que <span className="serif-accent">no se hacen en serie.</span>
                </>
              )}
            </h2>
            <p className="text-size-medium" style={{ maxWidth: '720px', color: 'var(--body-font)' }}>
              {description ||
                'Cada casa de Santa Mónica Residencial esconde piezas artesanales que la vuelven única. No es producción en línea: es trabajo de oficio.'}
            </p>
            {pool && totalInLightbox > displayCountActual && (
              <button
                type="button"
                className="craft-explore-link"
                onClick={() => setLightbox(0)}
                data-testid="craft-explore-all"
              >
                <span className="icon-font">photo_library</span>
                <span>Explorar todos los detalles ({totalInLightbox})</span>
              </button>
            )}
          </div>

          <div className={`sunset-grid${gridVariantClass}`}>
            {displayed.map((img, i) => (
              <button
                key={img.src + i}
                type="button"
                className={`sunset-tile sunset-tile-${i}`}
                onClick={() => setLightbox(i)}
                style={{
                  backgroundImage: `url(${img.src})`,
                  backgroundPosition: img.position || 'center',
                }}
                data-testid={`craft-tile-${i}`}
                aria-label={img.alt}
              >
                <span className="sunset-tile-zoom">
                  <span className="icon-font">zoom_in</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {lightbox !== null && fullSequence[lightbox] && (
        <div className="lightbox-modal" onClick={() => setLightbox(null)} data-testid="craft-lightbox">
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Cerrar">
            <span className="icon-font">close</span>
          </button>
          <button
            className="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((p) => (p - 1 + totalInLightbox) % totalInLightbox); }}
            aria-label="Anterior"
          >
            <span className="icon-font">chevron_left</span>
          </button>
          <img
            src={fullSequence[lightbox].src}
            alt={fullSequence[lightbox].alt}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="lightbox-next"
            onClick={(e) => { e.stopPropagation(); setLightbox((p) => (p + 1) % totalInLightbox); }}
            aria-label="Siguiente"
          >
            <span className="icon-font">chevron_right</span>
          </button>
          {totalInLightbox > 1 && (
            <div className="lightbox-counter" data-testid="lightbox-counter">
              {lightbox + 1} / {totalInLightbox}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ───────────── Datasets por modelo ───────────── */

export const fernandaCraftImages = [
  { src: '/images/real/sm-fer-craft-01-fachada.webp',          alt: 'Fachada del Modelo Fernanda',                    position: 'center 38%' },
  { src: '/images/real/sm-fer-craft-02-cocina.webp',           alt: 'Cocina con isla de granito y pendant lights',    position: 'center 70%' },
  { src: '/images/real/sm-fer-craft-03-roof.webp',             alt: 'Roof garden con muro de talavera',               position: '30% 40%' },
  { src: '/images/real/sm-fer-craft-04-lavabo.webp',           alt: 'Lavabo de talavera del medio baño',              position: 'center center' },
  { src: '/images/real/sm-fer-craft-05-escalera-frontal.webp', alt: 'Escalera con huellas de talavera',               position: 'center 55%' },
  { src: '/images/real/sm-fer-craft-06-recamara.webp',         alt: 'Recámara con ventana y closet de madera',        position: 'center 50%' },
  { src: '/images/real/sm-fer-craft-07-escalera-bano.webp',    alt: 'Detalle de escalera y medio baño con talavera',  position: 'center 50%' },
];

export const marianaCraftImages = [
  { src: '/images/real/sm-mar-craft-01-fachada-esquina.webp',  alt: 'Fachada esquinera de Mariana con letrero Avenida Santa Mónica', position: 'center 45%' },
  { src: '/images/real/sm-mar-craft-02-roof-pergola.webp',     alt: 'Roof garden con pérgola y celosía floral',                     position: 'center 55%' },
  { src: '/images/real/sm-mar-craft-03-cocina.webp',           alt: 'Cocina con isla y pendant lights, medio baño de talavera',     position: 'center 60%' },
  { src: '/images/real/sm-mar-craft-04-closet.webp',           alt: 'Walk-in closet de madera con acceso al baño',                  position: 'center center' },
  { src: '/images/real/sm-mar-craft-05-acceso-piedra.webp',    alt: 'Acceso con puerta de madera y muro de piedra laja',            position: 'center center' },
  { src: '/images/real/sm-mar-craft-06-sala-balcon.webp',      alt: 'Sala con puertas balconeras y vista al andador',               position: 'center 60%' },
  { src: '/images/real/sm-mar-craft-07-escalera.webp',         alt: 'Escalera de 3 niveles con barandal de hierro y peldaños de madera', position: 'center 55%' },
];

/* ────────────────── HOME · POOL EXPANDIBLE ──────────────────
   Solo detalles artesanales / mobiliario diseñado a propósito / gestos curados.
   Las fotos de vegetación y materia prima viven exclusivamente en el cintillo.
   El home toma `displayCount` (7) al azar para el mosaico; el lightbox recorre
   el pool completo. Para enriquecer la sección basta agregar más entradas aquí. */
export const homeCraftPool = [
  { src: '/images/details/detail-09.webp', alt: 'Fachada de piedra laja con letrero Santa Mónica en cobre forjado', position: 'center center' },
  { src: '/images/details/detail-11.webp', alt: 'Pérgola con celosía floral y listones de madera',                  position: 'center 60%' },
  { src: '/images/details/detail-08.webp', alt: 'Letrero peatonal Avenida Santa Mónica · Santa Mónica Residencial', position: 'center 60%' },
  { src: '/images/details/detail-03.webp', alt: 'Llaveros personalizados de Santa Mónica Residencial',              position: 'center center' },
  { src: '/images/details/detail-05.webp', alt: 'Columpio del parque diseñado a propósito',                         position: 'center 78%' },
  { src: '/images/details/detail-12.webp', alt: 'Kit de bienvenida con vino y termo Santa Mónica',                  position: 'center center' },
  { src: '/images/details/detail-15.webp', alt: 'Balcón de hierro forjado con jardinera de flores moradas',         position: 'center 55%' },
  { src: '/images/details/detail-14.webp', alt: 'Yucas con piedras blancas — paisajismo curado del andador',         position: 'center center' },
];
